import * as React from "react";
import {
  Box,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  TextField,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import moment from "moment";
import Cookies from "js-cookie";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://neuroez4.devforhealth.com/api";

const fmt = (d) => (d ? moment(d).format("MM/DD/YYYY") : "—");
const pad2 = (n) => String(n).padStart(2, "0");
const fmtUsDateTime = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = pad2(h);
  const min = pad2(d.getMinutes());
  return `${mm}/${dd}/${yyyy} ${hh}:${min} ${ampm}`;
};

async function fetchJSONSafe(url, options = {}) {
  const res = await fetch(url, {
    headers: { Accept: "application/json", ...(options.headers || {}) },
    ...options,
  });
  let data = {};
  try {
    const text = await res.text();
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }
  return { ok: res.ok, status: res.status, data };
}

const pickText = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

// Parse "Digitally signed by NAME on DATE"
const parseSignedMsg = (msg) => {
  if (!msg) return { name: "", dateText: "" };
  const m = String(msg).match(
    /digitally\s*signed\s*by\s*(.+?)\s*on\s*([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4}(?:\s+[0-9]{1,2}:[0-9]{2}\s*(?:AM|PM))?)/i
  );
  return m
    ? { name: (m[1] || "").trim(), dateText: (m[2] || "").replace(/\.+$/, "").trim() }
    : { name: "", dateText: "" };
};

// Build single-line signature: "Digitally signed by NAME on MM/DD/YYYY hh:mm AM." or "__"
const buildSignatureLine = (message, typedName, typedDate) => {
  const { name: nFromMsg, dateText } = parseSignedMsg(message);
  const nm = (nFromMsg || typedName || "").trim();
  const dt = dateText || fmtUsDateTime(typedDate);
  if (nm && dt !== "—") return `Digitally signed by ${nm} on ${dt}.`;
  return "__";
};

const expectedRows = [
  { key: "mentalhealthsymptoms", label: "Mental Health\nSymptoms" },
  { key: "mentalhealthserviceneeds", label: "Mental Health\nService Needs" },
  { key: "drugsalcohol", label: "Drugs/Alcohol" },
  { key: "vocational", label: "Vocational" },
  { key: "educational", label: "Educational" },
  { key: "social", label: "Social" },
  { key: "interpersonal", label: "Interpersonal" },
  { key: "selfcare", label: "Self-Care" },
  { key: "medical", label: "Medical" },
  { key: "dental", label: "Dental" },
  { key: "financial", label: "Financial" },
  { key: "housing", label: "Housing" },
  { key: "transportation", label: "Transportation" },
  { key: "other", label: "Other" },
];

const staffRoles = [
  { key: "staffMHProfessional", label: "MH Professional" },
  { key: "staffMHPractitioner", label: "MH Practitioner" },
  { key: "staffRehabWorker", label: "Rehab Worker" },
  { key: "staffRN", label: "RN" },
];

const supervisorRoles = [
  { key: "supervisorMHProfessional", label: "MH Professional" },
  { key: "supervisorMHPractitioner", label: "MH Practitioner" },
  { key: "supervisorRehabWorker", label: "Rehab Worker" },
  { key: "supervisorRN", label: "RN" },
];

const ARMHSFunctionalAssessmentNewPreview = ({
  patientFormId: propPatientFormId,
  id,
  data = {},
}) => {
  const patientFormId =
    propPatientFormId || id || data?.patientFormId || data?.id;

  const [form, setForm] = React.useState({});
  const [fa, setFa] = React.useState({});
  const [clientSigSrc, setClientSigSrc] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const LABEL_COL_WIDTH = 340;

  React.useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");
      setForm({});
      setFa({});
      setClientSigSrc("");

      try {
        const token = Cookies.get("authToken") || Cookies.get("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [formRes, faRes] = await Promise.all([
          fetchJSONSafe(`${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`, { headers }),
          fetchJSONSafe(`${API_BASE_URL}/Forms/getFunctionalassesmentById?id=${patientFormId}`, { headers }),
        ]);
        if (!active) return;

        if (!formRes.ok) throw new Error(`Forms request failed (${formRes.status})`);
        if (!faRes.ok) throw new Error(`Functional assessment request failed (${faRes.status})`);

        const formData = Array.isArray(formRes.data) ? (formRes.data[0] || {}) : (formRes.data || {});
        const faData = Array.isArray(faRes.data) ? (faRes.data[0] || {}) : (faRes.data || {});

        setForm(formData);
        setFa(faData);

        const clientSigRaw =
          formData?.clientsURL ||
          formData?.clientSignatureURL ||
          formData?.patientSignatureURL ||
          faData?.clientsURL ||
          faData?.clientSignatureURL ||
          faData?.patientSignatureURL ||
          "";

        if (clientSigRaw) {
          if (/^(https?:|data:|blob:)/i.test(clientSigRaw)) {
            if (active) setClientSigSrc(clientSigRaw);
          } else {
            const res = await fetch(
              `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(clientSigRaw)}`,
              { headers, signal: controller.signal }
            );
            if (active && res.ok) {
              const link = await res.text();
              if (link) setClientSigSrc(link);
            }
          }
        }
      } catch (e) {
        if (active) setError(e.message || "Failed to load data");
      } finally {
        if (active) setLoading(false);
      }
    };

    if (patientFormId) load();
    return () => {
      active = false;
      controller.abort();
    };
  }, [patientFormId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // Header + patient info
  const title = "ARMHS Functional Assessment (New)";
  const dos = fa.assessmentDate || form.assessmentDate || null;
  const fileVal = form.accountNo ?? "—";
  const nameVal = form.patientName || "—";
  const dobVal = form.dob || fa.patientDOB || null;
  const clientID = form.patientId || fa.patientID || "—";

  const timeFA = fa.initial ? "Initial" : fa.discharge ? "Discharge" : fa.ongoing ? "Ongoing" : "—";
  const faDate = fa.functionalDate || fa.assessmentDate || form.assessmentDate || null;
  const diagnosticDate = fa.diagnosticDate || null;

  // Table mappings
  const statusByKey = {
    mentalhealthsymptoms: fa.symptomsStatus,
    mentalhealthserviceneeds: fa.servicesStatus,
    drugsalcohol: fa.drugsalcoholStatus,
    vocational: fa.vocationalStatus,
    educational: fa.educationalStatus,
    social: fa.socialStatus,
    interpersonal: fa.interpersonalStatus,
    selfcare: fa.selfcareStatus,
    medical: fa.medicalStatus,
    dental: fa.dentalStatus,
    financial: fa.financialStatus,
    housing: fa.housingStatus,
    transportation: fa.transportationStatus,
    other: fa.otherStatus,
  };
  const funcByKey = {
    mentalhealthsymptoms: fa.symptomsFunctioning,
    mentalhealthserviceneeds: fa.servicesFunctioning,
    drugsalcohol: fa.drugsalcoholFunctioning,
    vocational: fa.vocationalFunctioning,
    educational: fa.educationalFunctioning,
    social: fa.socialFunctioning,
    interpersonal: fa.interpersonalFunctioning,
    selfcare: fa.selfcareFunctioning,
    medical: fa.medicalFunctioning,
    dental: fa.dentalFunctioning,
    financial: fa.financialFunctioning,
    housing: fa.housingFunctioning,
    transportation: fa.transportationFunctioning,
    other: fa.otherFunctioning,
  };
  const linkByKey = {
    mentalhealthsymptoms: fa.linkTosymptoms,
    mentalhealthserviceneeds: fa.linkToservices,
    drugsalcohol: fa.linkTodrugsalcohol,
    vocational: fa.linkTovocational,
    educational: fa.linkToeducational,
    social: fa.linkTosocial,
    interpersonal: fa.linkTointerpersonal,
    selfcare: fa.linkToselfcare,
    medical: fa.linkTomedical,
    dental: fa.linkTodental,
    financial: fa.linkTofinancial,
    housing: fa.linkTohousing,
    transportation: fa.linkTotransportation,
    other: fa.linkToother,
  };
  const linkMentalByKey = {
    mentalhealthsymptoms: fa.symptomsLinkToMental,
    mentalhealthserviceneeds: fa.servicesLinkToMental,
    drugsalcohol: fa.drugsAlcoholLinkToMental,
    vocational: fa.vocationalLinkToMental,
    educational: fa.educationalLinkToMental,
    social: fa.socialLinkToMental,
    interpersonal: fa.interpersonalLinkToMental,
    selfcare: fa.selfcareLinkToMental,
    medical: fa.medicalLinkToMental,
    dental: fa.dentalLinkToMental,
    financial: fa.financialLinkToMental,
    housing: fa.housingLinkToMental,
    transportation: fa.transportationLinkToMental,
    other: fa.otherLinkToMental,
  };

  // Plans
  const plans = Array.from({ length: 14 }, (_, i) => fa[`plan${i + 1}`]);
  const islicence = !!fa.islicence;

  // Staff name fields (display)
  const staffNameField = pickText(fa.staffName, form.staffName, "—");
  const supervisorNameField = pickText(
    fa.supervisorSignature,
    form.supervisorSignature,
    form.supervisorSignatureName,
    form.supervisedClinician,
    fa.staffSignature,
    "—"
  );
  const directorNameField = pickText(
    fa.directorSignature,
    form.directorSignature,
    "—"
  );
  const staffRoleKey = fa.version || "";
  const supervisorRoleKey = fa.versionSuper || "";

  // Client signature date
  const clientSigDate =
    form.patientSignatureDATE ||
    fa.patientSignatureDATE ||
    form.clientSignatureDATE ||
    fa.clientSignatureDATE ||
    null;

  // Signature messages
  const staffMsg =
    fa.messageStaffSignature ||
    fa.massagestaffSignature ||
    fa.message ||
    form.message ||
    "";
  const supervisorMsg =
    fa.messageSupervisorSignature ||
    fa.messageSupervisorSign ||
    form.messageSupervisorSignature ||
    form.messageSupervisorSign ||
    form.messagesupervisedClinician ||
    "";
  const directorMsg =
    fa.messagetreatmentdirector ||
    form.messagetreatmentdirector ||
    "";

  // Inline single-line signatures with expanded date fallbacks
  const staffSigLine = buildSignatureLine(
    staffMsg,
    pickText(fa.staffSignature, fa.staffName, ""),
    fa.staffdate || form.staffsignatureDATE || form.providerDate || fa.staffSignatureDATE || null
  );

  const supervisorSigLine = buildSignatureLine(
    supervisorMsg,
    supervisorNameField === "—" ? "" : supervisorNameField,
    fa.supervisordate ||
      form.supervisorSignatureDATE ||
      form.supervisorSignatureDate ||
      form.supervisorSignatureDatee ||
      form.supervisorDate ||
      form.SupervisorDate ||
      form.supervisedClinicianDate ||
      fa.supervisorSignatureDATE ||
      null
  );

  const directorSigLine = buildSignatureLine(
    directorMsg,
    directorNameField === "—" ? "" : directorNameField,
    fa.directordate || form.directorDate || fa.treatmentdirectorDate || null
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box sx={{ position: "sticky", top: "-16px", zIndex: (t) => t.zIndex.appBar, bgcolor: "#fff", p: 4, pb: 0, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {fmt(dos)}
        </Typography>
        <Typography variant="h6" sx={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", fontWeight: "bold" }}>
          {title}
        </Typography>
        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* Patient strip */}
      <Box sx={{ px: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>File #</strong> {fileVal}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <Typography variant="body2">
              <strong>Client Name:</strong> {nameVal}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "right" }}>
            <Typography variant="body2">
              <strong>DOB:</strong> {fmt(dobVal)}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2 }} />
      </Box>

      {/* Six info lines */}
      <Box sx={{ mt: 1, px: 4 }}>
        <Typography sx={{ m: 0 }}><b>Recipient Name:</b> {nameVal}</Typography>
        <Typography sx={{ m: 0 }}><b>Client DOB:</b> {fmt(dobVal)}</Typography>
        <Typography sx={{ m: 0 }}><b>Client ID#:</b> {clientID || "—"}</Typography>
        <Typography sx={{ m: 0 }}><b>Time of Functional Assessment</b> {timeFA}</Typography>
        <Typography sx={{ m: 0 }}><b>Functional Assessment Date:</b> {fmt(faDate)}</Typography>
        <Typography sx={{ m: 0 }}><b>Diagnostic Assessment Date:</b> {fmt(diagnosticDate)}</Typography>
      </Box>

      {/* Field | Status | Functioning | Link To Mental */}
      <Box sx={{ mt: 1, px: 4 }}>
        <TableContainer sx={{ width: "100%", border: "1px solid #ddd" }}>
          <Table size="small" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: "1px solid #ddd", fontWeight: 700, width: { md: "25%" } }}>Field</TableCell>
                <TableCell sx={{ border: "1px solid #ddd", fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ border: "1px solid #ddd", fontWeight: 700 }}>Functioning</TableCell>
                <TableCell sx={{ border: "1px solid #ddd", fontWeight: 700, width: { md: "22%" } }}>Link To Mental</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expectedRows.map((r, idx) => {
                const statusVal = pickText(statusByKey[r.key]) || "—";
                const funcVal = pickText(funcByKey[r.key], linkByKey[r.key]) || "—";
                const linkMentalVal = pickText(linkMentalByKey[r.key]) || "—";
                return (
                  <TableRow key={idx}>
                    <TableCell sx={{ border: "1px solid #ddd", verticalAlign: "top", fontWeight: 700, whiteSpace: "pre-line" }}>
                      {r.label}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #ddd", verticalAlign: "top", whiteSpace: "pre-wrap" }}>
                      {statusVal}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #ddd", verticalAlign: "top", whiteSpace: "pre-wrap" }}>
                      {funcVal}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #ddd", verticalAlign: "top", whiteSpace: "pre-wrap" }}>
                      {linkMentalVal}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Plan checkboxes 1..14 */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Typography sx={{ m: 0 }}>Check which of the areas are addressed in the Treatment Plan:</Typography>
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          {plans.map((checked, i) => (
            <Grid key={i} item xs="auto">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={!!checked} disabled size="small" sx={{ p: 0, mr: 1 }} />
                <Typography sx={{ m: 0 }}>{i + 1}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Summary */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Typography sx={{ m: 0 }}>
          Summarize the client’s strengths, resources, and all areas of functional impairment:
        </Typography>
        <TextareaAutosize
          minRows={3}
          value={fa.functionalImpairment || ""}
          readOnly
          style={{
            width: "100%",
            borderRadius: "8px",
            border: "2px solid #000",
            padding: "10px",
            resize: "none",
            marginTop: "6px",
          }}
        />
      </Box>

      {/* License holder checkbox */}
      <Box sx={{ mt: 1, px: 4, display: "flex", alignItems: "center" }}>
        <Checkbox checked={!!fa.islicence} disabled size="small" sx={{ p: 0, mr: 1 }} />
        <Typography sx={{ m: 0 }}>
          License holder did not contact the client’s family and other natural supports
        </Typography>
      </Box>

      {/* Staff Name row */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "12px", m: 0, width: LABEL_COL_WIDTH, flexShrink: 0 }}>
            Staff Name:
          </Typography>
          <TextField size="small" value={staffNameField} sx={{ flex: 1 }} InputProps={{ readOnly: true }} />
        </Box>
        <Grid container spacing={1} sx={{ mt: 0.5, pl: `${LABEL_COL_WIDTH}px` }}>
          {staffRoles.map((r) => (
            <Grid key={r.key} item xs="auto">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={fa.version === r.key || !!fa[r.key]} disabled size="small" sx={{ p: 0, mr: 1 }} />
                <Typography sx={{ m: 0 }}>{r.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Clinical Supervisor row */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "12px", m: 0, width: LABEL_COL_WIDTH, flexShrink: 0 }}>
            Clinical Supervisor:
          </Typography>
          <TextField size="small" value={supervisorNameField} sx={{ flex: 1 }} InputProps={{ readOnly: true }} />
        </Box>
        <Grid container spacing={1} sx={{ mt: 0.5, pl: `${LABEL_COL_WIDTH}px` }}>
          {supervisorRoles.map((r) => (
            <Grid key={r.key} item xs="auto">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={fa.versionSuper === r.key || !!fa[r.key]} disabled size="small" sx={{ p: 0, mr: 1 }} />
                <Typography sx={{ m: 0 }}>{r.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ARMHS Treatment Director Name row */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "12px", m: 0, width: LABEL_COL_WIDTH, flexShrink: 0 }}>
            ARMHS Treatment Director Name:
          </Typography>
          <TextField size="small" value={directorNameField} sx={{ flex: 1 }} InputProps={{ readOnly: true }} />
        </Box>
      </Box>

      {/* Signatures (inline) */}
      <Box sx={{ mt: 3, px: 4 }}>
        <Typography variant="h6" gutterBottom>Signatures</Typography>

        {/* Client (inline) */}
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: "bold", minWidth: 240 }}>Client Signature:</Typography>
          {clientSigSrc && (
            <img
              src={clientSigSrc}
              alt="Client signature"
              style={{
                width: 200,
                height: 80,
                border: "2px solid #000",
                background: "#f5f5f5",
                padding: 10,
                borderRadius: 8,
                objectFit: "contain",
              }}
            />
          )}
          <Typography sx={{ fontWeight: 600 }}>
            {clientSigDate ? `Signed on ${fmtUsDateTime(clientSigDate)}` : "__"}
          </Typography>
        </Box>

        {/* Staff Signature (inline) */}
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: "bold", minWidth: 240 }}>Staff Signature (required):</Typography>
          <Typography sx={{ fontWeight: 600 }}>{staffSigLine}</Typography>
        </Box>

        {/* Clinical Supervisor (inline) */}
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: "bold", minWidth: 240 }}>Clinical Supervisor:</Typography>
          <Typography sx={{ fontWeight: 600 }}>{supervisorSigLine}</Typography>
        </Box>

        {/* ARMHS Treatment Director (inline) */}
        <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: "bold", minWidth: 240 }}>ARMHS Treatment Director:</Typography>
          <Typography sx={{ fontWeight: 600 }}>{directorSigLine}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ARMHSFunctionalAssessmentNewPreview;